/**
 * Countdown helper functions for displaying time remaining
 */

/**
 * Calculate time remaining until a target date
 * @param {string|Date} targetDate - The target date
 * @returns {Object} Object with time components
 */
export function getTimeRemaining(targetDate) {
    const now = new Date().getTime();
    const target = new Date(targetDate).getTime();
    const difference = target - now;

    if (difference <= 0) {
        return {
            total: 0,
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            expired: true
        };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return {
        total: difference,
        days,
        hours,
        minutes,
        seconds,
        expired: false
    };
}

/**
 * Format time remaining as a human-readable string
 * @param {Object} timeRemaining - Object from getTimeRemaining
 * @returns {string} Formatted time string
 */
export function formatTimeRemaining(timeRemaining) {
    if (timeRemaining.expired) {
        return 'Poll has ended';
    }

    const parts = [];
    
    if (timeRemaining.days > 0) {
        parts.push(`${timeRemaining.days} ${timeRemaining.days === 1 ? 'Day' : 'Days'}`);
    }
    
    if (timeRemaining.hours > 0) {
        parts.push(`${timeRemaining.hours} ${timeRemaining.hours === 1 ? 'Hour' : 'Hours'}`);
    }
    
    if (timeRemaining.minutes > 0) {
        parts.push(`${timeRemaining.minutes} ${timeRemaining.minutes === 1 ? 'Minute' : 'Minutes'}`);
    }
    
    if (timeRemaining.seconds > 0 && timeRemaining.days === 0) {
        parts.push(`${timeRemaining.seconds} ${timeRemaining.seconds === 1 ? 'Second' : 'Seconds'}`);
    }

    if (parts.length === 0) {
        return 'Less than a minute';
    }

    return `Ends in ${parts.join(', ')}`;
}

/**
 * Format time remaining as a short string (e.g., "2d 3h 45m")
 * @param {Object} timeRemaining - Object from getTimeRemaining
 * @returns {string} Short formatted time string
 */
export function formatTimeRemainingShort(timeRemaining) {
    if (timeRemaining.expired) {
        return 'Ended';
    }

    const parts = [];
    
    if (timeRemaining.days > 0) {
        parts.push(`${timeRemaining.days}d`);
    }
    
    if (timeRemaining.hours > 0) {
        parts.push(`${timeRemaining.hours}h`);
    }
    
    if (timeRemaining.minutes > 0) {
        parts.push(`${timeRemaining.minutes}m`);
    }
    
    if (timeRemaining.seconds > 0 && timeRemaining.days === 0) {
        parts.push(`${timeRemaining.seconds}s`);
    }

    if (parts.length === 0) {
        return '<1m';
    }

    return `Ends in ${parts.join(' ')}`;
}
